import type { QueryResultSet } from '../../../../types/queryExecution';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';

function pickRowValues(args: {
  row: Record<string, unknown>;
  columns: TableExplorerColumn[];
}): Record<string, unknown> {
  const rowKeyByNormalized = new Map<string, string>();
  for (const key of Object.keys(args.row)) {
    rowKeyByNormalized.set(normalizeColumnName(key), key);
  }

  const picked: Record<string, unknown> = {};
  for (const column of args.columns) {
    const normalized = normalizeColumnName(column.name);
    const rowKey = rowKeyByNormalized.get(normalized) ?? column.name;
    picked[column.name] = args.row[rowKey];
  }

  return picked;
}

export function buildTableExplorerExportResultSet(args: {
  rows: Record<string, unknown>[];
  columns: TableExplorerColumn[];
  selectedColumns: string[] | '*';
  totalRowCount?: number;
  /**
   * D3 — INSERT copy/export parity. Table Explorer always knows its table
   * outright (unlike a QE SELECT result set, which must infer provenance
   * from parsed source columns), so when `table` is provided every column
   * carries `sourceSchema`/`sourceTable`/`sourceColumn`. That's the exact
   * shape `insertStatementBuilder`'s `resolveSingleSourceTable` reads to
   * auto-resolve (and correctly quote, per-engine) the target table without
   * TE having to duplicate the builder's quoting logic. Omit for schema-less
   * engines (MySQL/SQLite) by passing `''`. Every other export format
   * (csv/xlsx/json/markdown) ignores these fields.
   */
  schema?: string;
  table?: string;
}): QueryResultSet {
  const visibleColumns =
    args.selectedColumns === '*'
      ? args.columns
      : args.selectedColumns
          .map((name) => args.columns.find((col) => col.name === name))
          .filter((col): col is TableExplorerColumn => col !== undefined);

  return {
    columns: visibleColumns.map((col) => ({
      name: col.name,
      dataType: col.dataType,
      nullable: col.nullable,
      isPrimaryKey: col.isPrimaryKey,
      isForeignKey: col.isForeignKey,
      ...(args.table
        ? {
            sourceSchema: args.schema || undefined,
            sourceTable: args.table,
            sourceColumn: col.name,
          }
        : {}),
    })),
    rows: args.rows.map((row) => pickRowValues({ row, columns: visibleColumns })),
    rowCount: args.totalRowCount ?? args.rows.length,
  };
}

